import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import type { EventItem } from '../../types/shared/event';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';

interface CategoryFilterProps {
  selectedCategory: string;
  selectedType: string;
  onCategoryChange: (category: string) => void;
  onTypeChange: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  items: EventItem[];
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  selectedCategory,
  selectedType,
  onCategoryChange,
  onTypeChange,
  config,
  translations,
  items = []
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'event-api'
  });

  // Default translations
  const defaultTranslations = {
    filter_all_categories: {
      fr: 'Toutes les catégories',
      en: 'All categories',
      es: 'Todas las categorías',
      it: 'Tutte le categorie',
      pt: 'Todas as categorias',
      de: 'Alle Kategorien',
      nl: 'Alle categorieën',
      ja: 'すべてのカテゴリー',
      zh: '所有类别',
      ru: 'Все категории'
    },
    filter_all_types: {
      fr: 'Tous les types',
      en: 'All types',
      es: 'Todos los tipos',
      it: 'Tutti i tipi',
      pt: 'Todos os tipos',
      de: 'Alle Typen',
      nl: 'Alle types',
      ja: 'すべてのタイプ',
      zh: '所有类型',
      ru: 'Все типы'
    }
  };

  // Mémoriser les catégories uniques
  const categories = React.useMemo(() => 
    Array.from(new Set(items.map(item => item.category))),
    [items]
  );

  // Mémoriser les types pour la catégorie sélectionnée
  const types = React.useMemo(() => {
    if (!selectedCategory) return [];
    return Array.from(new Set(
      items
        .filter(item => item.category === selectedCategory)
        .map(item => item.type)
        .filter(Boolean)
    ));
  }, [items, selectedCategory]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    analytics.trackFilter('category_filter', category);
    onCategoryChange(category);
    onTypeChange('');
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    analytics.trackFilter('type_filter', type);
    onTypeChange(type);
  };

  const getTranslatedText = (key: string): string => {
    const translation = translations[key] || defaultTranslations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  return (
    <div 
      className="flex border-t border-white/30"
      style={{ backgroundColor: config['major-color'] }}
    >
      {/* Category Dropdown */}
      <div 
        className={`px-4 py-3 flex items-center justify-between relative ${
          selectedCategory && types.length > 0 ? 'w-1/2' : 'w-full'
        }`}
        style={{ 
          borderRight: selectedCategory && types.length > 0 
            ? '1px solid rgba(255, 255, 255, 0.2)' 
            : 'none'
        }}
      >
        <select
          value={selectedCategory}
          onChange={handleCategoryChange}
          className="bg-transparent text-white appearance-none cursor-pointer pr-8 focus:outline-none w-full truncate"
          style={{ color: config['light-color'] }}
          aria-label="Sélectionner une catégorie"
        >
          <option value="">{getTranslatedText('filter_all_categories')}</option>
          {categories.map(category => (
            <option key={category} value={category}>
              {getTranslatedCategory(category)}
            </option>
          ))}
        </select>
        <ChevronDown 
          size={20} 
          className="absolute right-4 text-white pointer-events-none"
          style={{ color: config['light-color'] }}
        />
      </div>

      {/* Type Dropdown - Only show if category is selected and types exist */}
      {selectedCategory && types.length > 0 && (
        <div 
          className="w-1/2 px-4 py-3 flex items-center justify-between relative"
        >
          <select
            value={selectedType}
            onChange={handleTypeChange}
            className="bg-transparent text-white appearance-none cursor-pointer pr-8 focus:outline-none w-full truncate"
            style={{ color: config['light-color'] }}
            aria-label="Sélectionner un type"
          >
            <option value="">{getTranslatedText('filter_all_types')}</option>
            {types.map(type => (
              <option key={type} value={type}>
                {getTranslatedCategory(type)}
              </option>
            ))}
          </select>
          <ChevronDown 
            size={20} 
            className="absolute right-4 text-white pointer-events-none"
            style={{ color: config['light-color'] }}
          />
        </div>
      )}
    </div>
  );
};